import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronDown, MapPin, BarChart2, FileText, Star, Map, RefreshCw, Loader, Settings, AlertTriangle } from 'lucide-react'
import { useApi, asArray } from '../hooks/useApi'
import { useGbpScope } from '../hooks/useGbpScope'
import { GbpReconsentBanner } from '../components/GbpReconsentBanner'
import { GbpNotManagerNotice } from '../components/GbpNotManagerNotice'
import { Insights } from './local/Insights'
import { Posts } from './local/Posts'
import { Reputation } from './local/Reputation'
import Maps from './local/Maps'
import Updates from './local/Updates'

interface Client {
  id: number
  name: string
  domain: string
}

interface Location {
  id: number
  client_id: number
  name: string
  address: string | null
  place_id: string | null
  gbp_location_id: string | null
  is_active: boolean
  accessErrorCode?: string | null
}

type Tab = 'insights' | 'maps' | 'reputation' | 'posts' | 'updates'

const TABS: { key: Tab; label: string; icon: typeof BarChart2 }[] = [
  { key: 'insights', label: 'Insights', icon: BarChart2 },
  { key: 'maps', label: 'Positions Maps', icon: Map },
  { key: 'reputation', label: 'R\u00e9putation', icon: Star },
  { key: 'posts', label: 'Publications', icon: FileText },
  { key: 'updates', label: 'Mises \u00e0 jour', icon: RefreshCw },
]

function getTabFromPath(pathname: string): Tab {
  if (pathname.includes('/local/maps')) return 'maps'
  if (pathname.includes('/local/reputation')) return 'reputation'
  if (pathname.includes('/local/posts')) return 'posts'
  if (pathname.includes('/local/updates')) return 'updates'
  return 'insights'
}

export default function Local() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
  const [selectorOpen, setSelectorOpen] = useState(false)

  const activeTab = getTabFromPath(location.pathname)

  const { data: scope, loading: scopeLoading } = useGbpScope()

  const { data: client, loading: clientLoading, error: clientError } = useApi<Client>(
    `/api/clients/${projectId}`
  )

  const { data: locations, loading: locationsLoading } = useApi<Location[]>(
    `/api/local/clients/${projectId}/locations`
  )

  // Auto-select first location when loaded
  // Must run BEFORE any early return to respect Rules of Hooks
  useEffect(() => {
    if (asArray(locations).length > 0 && selectedLocationId === null) {
      setSelectedLocationId(asArray(locations)[0].id)
    }
  }, [locations, selectedLocationId])

  // Redirect on 401
  if (clientError === 'Unauthorized' || clientError === '401') {
    navigate('/login')
    return null
  }

  const locationList = asArray(locations)
  const selectedLocation = locationList.find(l => l.id === selectedLocationId) ?? null
  const loading = clientLoading || locationsLoading

  function handleTabClick(tab: Tab) {
    const base = location.pathname.split('/local/')[0]
    navigate(`${base}/local/${tab}`)
  }

  return (
    <div className="text-[#1B2559]">
      {/* Click outside to close selector */}
      {selectorOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setSelectorOpen(false)} />
      )}

      {/* GBP reconsent banner */}
      {!scopeLoading && scope && !scope.hasBusinessManage && <GbpReconsentBanner />}

      {/* Top bar: client name + GBP fiche selector */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="min-w-0">
          {client ? (
            <p className="font-semibold text-[#1B2559] truncate">{client.name}</p>
          ) : (
            <div className="h-5 w-40 bg-[#F4F5FA] rounded animate-pulse" />
          )}
        </div>

        {/* GBP fiche selector */}
        <div className="relative shrink-0">
          <button
            onClick={() => setSelectorOpen(v => !v)}
            disabled={loading || locationList.length === 0}
            className="flex items-center gap-2 px-3.5 py-2 bg-white border border-[#E2E4ED] rounded-xl text-sm text-[#1B2559] hover:border-[#C8CBD8] transition-all disabled:opacity-50 shadow-sm"
          >
            {loading ? (
              <Loader className="w-4 h-4 animate-spin text-[#555A73]" strokeWidth={1.25} />
            ) : (
              <MapPin className="w-4 h-4 text-[#6366F1]" strokeWidth={1.25} />
            )}
            <div className="text-left max-w-[200px]">
              <p className="text-sm font-medium text-[#1B2559] truncate leading-tight">
                {selectedLocation?.name ?? (locationList.length === 0 ? 'Aucune fiche connect&eacute;e' : 'Choisir une fiche…')}
              </p>
              {selectedLocation?.address && (
                <p className="text-xs text-[#8B8FA7] truncate leading-tight">{selectedLocation.address}</p>
              )}
            </div>
            {locationList.length > 1 && (
              <span className="ml-1 flex items-center justify-center w-5 h-5 rounded-full bg-[#EEF2FF] text-[#6366F1] text-xs font-semibold shrink-0">
                {locationList.length}
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-[#555A73] shrink-0" strokeWidth={1.25} />
          </button>

          {selectorOpen && locationList.length > 0 && (
            <div className="absolute right-0 top-full mt-1.5 w-72 bg-white border border-[#E2E4ED] rounded-xl shadow-xl z-50 overflow-hidden">
              <div className="px-3 py-2 border-b border-[#E2E4ED]">
                <p className="text-xs font-medium text-[#8B8FA7] uppercase tracking-wide">
                  {locationList.length} fiche{locationList.length > 1 ? 's' : ''} connect&eacute;{locationList.length > 1 ? 'es' : 'e'}
                </p>
              </div>
              {locationList.map(loc => (
                <button
                  key={loc.id}
                  onClick={() => {
                    setSelectedLocationId(loc.id)
                    setSelectorOpen(false)
                  }}
                  className={`w-full text-left px-4 py-3 flex items-start gap-2.5 hover:bg-[#F4F5FA] transition-colors ${
                    loc.id === selectedLocationId ? 'bg-[#EEF2FF]' : ''
                  }`}
                >
                  <MapPin
                    className={`w-4 h-4 shrink-0 mt-0.5 ${loc.id === selectedLocationId ? 'text-[#6366F1]' : 'text-[#8B8FA7]'}`}
                    strokeWidth={1.25}
                  />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm truncate flex items-center gap-1.5 ${loc.id === selectedLocationId ? 'text-[#6366F1] font-medium' : 'text-[#1B2559]'}`}>
                      {loc.name}
                      {loc.accessErrorCode === 'NOT_MANAGER' && (
                        <AlertTriangle
                          className="w-3.5 h-3.5 text-amber-600 shrink-0"
                          strokeWidth={1.75}
                          aria-label="Fiche non g&eacute;r&eacute;e par le compte connect&eacute;"
                        />
                      )}
                    </p>
                    {loc.address && (
                      <p className="text-xs text-[#8B8FA7] truncate">{loc.address}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fiche non geree par le compte connecte */}
      {selectedLocation?.accessErrorCode === 'NOT_MANAGER' && (
        <GbpNotManagerNotice projectId={projectId ?? ''} />
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b border-[#E2E4ED] mb-6 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon
          const isActive = activeTab === tab.key
          return (
            <button
              key={tab.key}
              onClick={() => handleTabClick(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                isActive
                  ? 'border-[#6366F1] text-[#1B2559]'
                  : 'border-transparent text-[#8B8FA7] hover:text-[#1B2559]'
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.25} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Main content */}
      <div>
        {/* No locations — empty state */}
        {!loading && locationList.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4 shadow-sm">
              <MapPin className="w-7 h-7 text-[#6366F1]" strokeWidth={1.25} />
            </div>
            <p className="text-[#1B2559] font-semibold mb-1.5">Aucune fiche GBP connect&eacute;e</p>
            <p className="text-[#8B8FA7] text-sm max-w-xs mb-5">
              Connectez une fiche Google Business Profile pour acc&eacute;der aux insights, positions et avis de cet &eacute;tablissement.
            </p>
            <button
              onClick={() => navigate(`/p/${projectId}/settings/data-sources`)}
              className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] text-white rounded-xl text-sm font-medium hover:bg-[#4F46E5] transition-colors"
            >
              <Settings className="w-4 h-4" strokeWidth={1.25} />
              Sources de donn&eacute;es
            </button>
          </div>
        )}

        {/* Tab content */}
        {(!loading || selectedLocationId !== null) && locationList.length > 0 && (
          <>
            {activeTab === 'insights' && <Insights locationId={selectedLocationId} />}
            {activeTab === 'maps' && <Maps locationId={selectedLocationId} />}
            {activeTab === 'reputation' && <Reputation locationId={selectedLocationId} />}
            {activeTab === 'posts' && <Posts locationId={selectedLocationId} projectId={projectId ?? ''} />}
            {activeTab === 'updates' && <Updates locationId={selectedLocationId} />}
          </>
        )}

        {/* Loading state */}
        {loading && !selectedLocationId && (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  )
}
